import { stableStringify } from '@provision/formatting-utils';
import { QueryHookOptions, useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import {
  ConversationMessageDraft,
  ConversationMessageDraftSendAnd,
  ConversationMessageDraftSendUnless,
  mapDraft,
  useCurrentUserProfile,
} from 'src/graphql';
import { UPSERT_CONVERSATION_MESSAGE_DRAFT_QUERY } from 'src/graphql/entities/conversation-message-draft/conversation-message-draft.operations';
import { ConversationMessageDraftRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import { upsertDraftInCache } from './utils';

export interface UpsertConversationMessageDraft {
  upsertConversationMessageDraft: ConversationMessageDraftRaw & {
    __typename: 'ConversationMessageDraft';
  };
}

export interface DraftAttachmentInput {
  fileKey: string;
  fileName: string;
  contentType?: string | null;
  size?: number | null;
}

const SCHEDULING_THRESHOLD_MS = 30_000;

export const useUpsertConversationMessageDraft = () => {
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useNotifications();

  const [mutate, { loading: isLoading }] = useMutation<UpsertConversationMessageDraft>(
    UPSERT_CONVERSATION_MESSAGE_DRAFT_QUERY,
    {
      update(cache, { data }) {
        const draft = data?.upsertConversationMessageDraft;
        if (!draft) return;

        upsertDraftInCache(cache, mapDraft(draft));
      },
    },
  );

  const upsert = useCallback(
    (
      params: {
        conversationId: string;
        partnerId: number;
        message?: string;
        messageHtml?: string | null;
        isPrivate: boolean;
        scheduledAt?: string | null;
        sideEffects?: ConversationMessageDraftSendAnd | null;
        sendUnless?: ConversationMessageDraftSendUnless[] | null;
        discardedAt?: string | null;
        attachments?: DraftAttachmentInput[];
      },
      conversation: ConversationMessageDraft['conversation'],
      onCompleted?: QueryHookOptions['onCompleted'],
    ) => {
      if (!user) return;

      // guard - do not allow scheduling in the past or too close to now
      if (params.scheduledAt) {
        const target = new Date(params.scheduledAt);
        const nowPlusBuffer = Date.now() + SCHEDULING_THRESHOLD_MS;

        if (!Number.isFinite(target.getTime()) || target.getTime() <= nowPlusBuffer) {
          showErrorAlert('Scheduled time must be at least 30 seconds in the future.');
          return;
        }
      }

      const now = new Date().toISOString();
      const id = `optimistic-${now}`;

      const scheduledData = {
        scheduledAt: params.scheduledAt ?? '',
        sideEffects: params.sideEffects
          ? stableStringify({
              actions: params.sideEffects.actions.map((action) => ({
                type: action.type,
                value: action.value ?? '',
              })),
            })
          : '',
        sendUnless: params.sendUnless
          ? stableStringify(
              params.sendUnless.map((item) => ({
                type: item.type,
                value: item.value ?? '',
              })),
            )
          : '',
        discardedAt: params.discardedAt ?? '',
      };
      return mutate({
        errorPolicy: 'all',
        variables: { params: { ...params, ...scheduledData } },
        optimisticResponse: {
          upsertConversationMessageDraft: {
            __typename: 'ConversationMessageDraft',
            id,
            conversationMessageDraftId: id,
            conversation: {
              // @ts-expect-error that's enough fields for optimistic update
              __typename: 'Conversation',
              id: conversation.id,
              conversationId: conversation.conversationId,
              subject: conversation.subject,
              workspaceConversationId: conversation.workspaceConversationId,
              conversationStatusId: conversation.conversationStatusId,
            },
            conversationId: params.conversationId,
            userId: user.id,
            message: params.message ?? '',
            messageHtml: params.messageHtml ?? '',
            isPrivate: params.isPrivate,
            partnerId: params.partnerId,
            source: '',
            attachments: (params.attachments ?? []).map((attachment, index) => ({
              __typename: 'ConversationAttachment',
              id: `${id}-attachment-${index}`,
              conversationAttachmentId: `${id}-attachment-${index}`,
              conversationId: params.conversationId,
              partnerId: params.partnerId,
              fileKey: attachment.fileKey,
              fileName: attachment.fileName,
              contentType: attachment.contentType ?? null,
              size: attachment.size ?? null,
            })) as unknown as ConversationMessageDraftRaw['attachments'],
            createdAt: now,
            updatedAt: now,
            ...scheduledData,
          },
        },
        onCompleted,
        context: { omitIsolationModeHeader: true },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate, user],
  );

  return { upsertConversationMessageDraft: upsert, isLoading };
};
